import { Rows } from '../../interfaces';
import { CellObject, CellStyle } from "xlsx-js-style";
import { formats, getCell, withCellStyles } from "../../utils";
import {
  EvaluationTotalForm,
  PortfolioEvaluationForm,
} from "../../../company-performance/business-evaluation/dto/EvaluationAuxiliary";

// Main function to prepare data for export
export const getPreparedData = (evaluations: PortfolioEvaluationForm[], total: EvaluationTotalForm, headers: string[]): Rows => {
  const rows: Rows = [headers]; // Initialize rows with headers
  rows.push(...mapEvaluationsToRows(evaluations, headers)); // Add evaluation rows
  rows.push(mapTotalsToRow(total, headers)); // Add totals row
  return rows;
};

// Maps evaluations to rows based on headers
const mapEvaluationsToRows = (evaluations: PortfolioEvaluationForm[], headers: string[]): Rows => {
  return evaluations.map((evaluation): any[] => mapDataToRow(getEvaluationMapping(evaluation), headers, left));
};

// Maps totals to a row based on headers
const mapTotalsToRow = (totals: EvaluationTotalForm, headers: string[]): Rows => {
  const totalMapping = getTotalsMapping(totals);
  return mapDataToRow(totalMapping, headers, boldLightGrayBgLeft) as Rows;
};

// Maps evaluation data to a structured object
const getEvaluationMapping = (evaluationForm: PortfolioEvaluationForm): MappedEvaluationRow => {
  const { name, ranking, weight, score, comment } = evaluationForm.value;
  return {
    Parameter: name,
    Ranking: ranking.rank,
    Weight: createPercentCell(weight),
    Score: score,
    Details: comment,
  };
};

// Maps total data to a structured object
const getTotalsMapping = (evaluationTotals: EvaluationTotalForm): MappedEvaluationRow => {
  return {
    Parameter: 'Total',
    Ranking: '',
    Weight: createPercentCell(evaluationTotals.controls.weight.value),
    Score: evaluationTotals.controls.score.value,
    Details: '',
  };
};

// Creates a percentage cell for Excel with consistent formatting
const createPercentCell = (percentValue: number): Partial<CellObject> => {
  return getCell({
    f: (percentValue / 100).toString(), // Convert from percentage * 100 to decimal
    z: formats.percents,
    t: 'n',
  });
};

// Maps structured data to a row based on headers and applies a default cell style
const mapDataToRow = (mappedData: MappedEvaluationRow, headers: string[], style: CellStyle): any[] => {
  return headers.map((header) => withCellStyles(mappedData[header], style));
};

export const boldLightGrayBgLeft: CellStyle = {
  font: { bold: true },
  fill: { fgColor: { rgb: 'e3e3e3' } },
  alignment: { horizontal: 'left' },
};

export const left: CellStyle = {
  alignment: { horizontal: 'left' },
};

interface MappedEvaluationRow {
  'Parameter': string;
  'Ranking': string;
  'Weight': Partial<CellObject>;
  'Score': number | string;
  'Details': string;
}
